import type { ReactNode } from "react";
import Head from "next/head";
import Image from "next/image";
import Link from "next/link";
import { HomeFooter } from "@/components/boardblend/home-footer";
import { LegalHeader } from "@/components/boardblend/legal-header";
import type { SeoPageMetadata } from "@/lib/seo-content-types";
import {
  SITE_ORGANIZATION_ID,
  SITE_ORIGIN,
} from "@/lib/public-seo";
import { SeoCta } from "./seo-cta";
import { SeoScrollPage } from "./seo-scroll-page";
import styles from "./seo-page.module.css";

export function SeoPageTemplate({
  children,
  metadata,
}: {
  children: ReactNode;
  metadata: SeoPageMetadata;
}) {
  const image = metadata.hero
    ? `${SITE_ORIGIN}${metadata.hero.src}`
    : `${SITE_ORIGIN}/og-boardblend.png`;
  const structuredData = buildStructuredData(metadata, image);

  return (
    <>
      <Head>
        <title>{metadata.seoTitle}</title>
        <meta name="description" content={metadata.description} />
        <link rel="canonical" href={metadata.canonical} />
        {metadata.status === "draft" && (
          <meta name="robots" content="noindex,nofollow" />
        )}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="Boardblend" />
        <meta property="og:locale" content="en_CA" />
        <meta property="og:title" content={metadata.seoTitle} />
        <meta property="og:description" content={metadata.description} />
        <meta property="og:url" content={metadata.canonical} />
        <meta property="og:image" content={image} />
        <meta property="og:image:width" content="1600" />
        <meta property="og:image:height" content="900" />
        <meta property="og:image:alt" content={metadata.hero?.alt} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metadata.seoTitle} />
        <meta name="twitter:description" content={metadata.description} />
        <meta name="twitter:image" content={image} />
        <meta name="twitter:image:alt" content={metadata.hero?.alt} />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(structuredData).replace(/</gu, "\\u003c"),
          }}
        />
      </Head>

      <SeoScrollPage routeKey={metadata.slug}>
        <LegalHeader />
        <article className={styles.article}>
          <header className={styles.hero}>
            <nav className={styles.breadcrumbs} aria-label="Breadcrumb">
              <Link href="/">Home</Link>
              <span aria-hidden="true">/</span>
              <Link href="/resources">Resources</Link>
            </nav>
            <p className={styles.eyebrow}>{formatType(metadata.type)}</p>
            <h1>{metadata.title}</h1>
            <p className={styles.description}>{metadata.description}</p>
            <p className={styles.byline}>
              By {metadata.author}
              {metadata.updatedAt && (
                <> · Updated {formatDate(metadata.updatedAt)}</>
              )}
              {metadata.reviewedAt && (
                <> · Reviewed {formatDate(metadata.reviewedAt)}</>
              )}
            </p>
            {metadata.hero && (
              <div className={styles.heroImage}>
                <Image
                  fill
                  priority
                  alt={metadata.hero.alt}
                  sizes="(max-width: 800px) 100vw, 1120px"
                  src={metadata.hero.src}
                  unoptimized={metadata.hero.src.endsWith(".svg")}
                />
              </div>
            )}
          </header>

          <div className={styles.body}>{children}</div>

          {metadata.faqs.length > 0 && (
            <section className={styles.faqs} aria-labelledby="faq-heading">
              <p className={styles.eyebrow}>COMMON QUESTIONS</p>
              <h2 id="faq-heading">Frequently asked questions</h2>
              <div>
                {metadata.faqs.map((faq) => (
                  <details key={faq.question}>
                    <summary>{faq.question}</summary>
                    <p>{faq.answer}</p>
                  </details>
                ))}
              </div>
            </section>
          )}

          <SeoCta />
        </article>
        <HomeFooter />
      </SeoScrollPage>
    </>
  );
}

function buildStructuredData(metadata: SeoPageMetadata, image: string) {
  const graph: Record<string, unknown>[] = [
    {
      "@type": "Article",
      "@id": `${metadata.canonical}#article`,
      headline: metadata.title,
      description: metadata.description,
      url: metadata.canonical,
      mainEntityOfPage: { "@type": "WebPage", "@id": metadata.canonical },
      image: {
        "@type": "ImageObject",
        url: image,
        width: 1600,
        height: 900,
      },
      author: {
        "@type": "Organization",
        "@id": SITE_ORGANIZATION_ID,
        name: metadata.author,
        url: `${SITE_ORIGIN}/`,
      },
      publisher: {
        "@type": "Organization",
        "@id": SITE_ORGANIZATION_ID,
        name: "Boardblend",
        url: `${SITE_ORIGIN}/`,
      },
      ...(metadata.publishedAt && { datePublished: metadata.publishedAt }),
      ...(metadata.updatedAt && { dateModified: metadata.updatedAt }),
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: SITE_ORIGIN,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Resources",
          item: `${SITE_ORIGIN}/resources`,
        },
        {
          "@type": "ListItem",
          position: 3,
          name: metadata.title,
          item: metadata.canonical,
        },
      ],
    },
  ];

  if (metadata.faqs.length > 0) {
    graph.push({
      "@type": "FAQPage",
      mainEntity: metadata.faqs.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}

function formatDate(date: string) {
  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "long",
    timeZone: "UTC",
    year: "numeric",
  }).format(new Date(`${date}T00:00:00Z`));
}

function formatType(type: SeoPageMetadata["type"]) {
  if (type === "comparison") return "PRODUCT COMPARISON";
  if (type === "roundup") return "TOOL GUIDE";
  return "PRACTICAL GUIDE";
}
